// Phase 3 report export — PDF (jsPDF + autotable) and DOCX (html-docx-js, as the Phase 1 downloads).
// One builder turns the stored report JSON into plain blocks that mirror ReportView's sections; the two
// renderers only lay those blocks out. Loaded on demand from the HM Review tab.
import { verdictLabel, INTEGRITY_CONFIDENCE_HELP, BAND_LABELS, ADVISORY_FOOTER, reviewLabel, riskLevelLabel,
  roleFrameText } from '../components/Phase3InterviewDetail';

// Same thresholds as ReportView's score bar (used when the report has no skill_band)
const bandFromScore = (s) => (s >= 320 ? 'EXCEEDS_BAR' : s >= 200 ? 'MEETS_BAR' : s >= 150 ? 'BORDERLINE' : 'BELOW_BAR');

export function parseReportContent(report) {
  const raw = report?.report_content;
  if (raw && typeof raw === 'object') return raw;
  try { return raw ? JSON.parse(raw) : {}; } catch (e) { return {}; }
}

const q = (s) => `"${s}"`;

// Blocks: {t:'h'|'sub'|'p'|'note'|'warn'|'bullets'|'kv'|'table', ...}
export function buildReportBlocks(content, { typeLabel = 'Hiring Manager', assessment, questionSources } = {}) {
  const c = content || {};
  const isHM = typeLabel === 'Hiring Manager';
  const out = [];
  const sb = c.score_bar || {};
  const score = sb.overall_score ?? c.headline?.match(/\d+/)?.[0] ?? 0;
  const pb = c.performance_benchmarking || {};
  const evidence = c.evidence_status || assessment?.evidence_status;
  const reason = c.verdict_reason || assessment?.verdict_reason;
  const band = c.skill_band || assessment?.skill_band || bandFromScore(Number(score));
  const showPct = (c.percentiles_shown ?? assessment?.percentiles_shown ?? true) && pb.shown !== false;

  // 1. Headline + score
  if (c.headline) out.push({ t: 'h', text: c.headline });
  if (c.executive_summary) out.push({ t: 'p', text: c.executive_summary });
  const kv = [['Overall score', `${sb.overall_score ?? score} / 400`]];
  if (sb.recommendation) kv.push(['Verdict', verdictLabel(sb.recommendation, reason)]);
  kv.push(['Skill score band', BAND_LABELS[band] || band]);
  if (reason?.type === 'integrity_override' && reason.detail) kv.push(['Reason', reason.detail]);
  if (showPct && (pb.role_percentile || pb.all_roles_percentile)) {
    kv.push(['Role percentile', `${pb.role_percentile ?? '—'}th${pb.role_cohort_size > 0 ? ` (vs ${pb.role_cohort_size} candidates)` : ''}`]);
    kv.push(['All-IT percentile', `${pb.all_roles_percentile ?? '—'}th${pb.all_roles_cohort_size > 0 ? ` (vs ${pb.all_roles_cohort_size} candidates)` : ''}`]);
  }
  out.push({ t: 'kv', rows: kv });
  if (reason?.coverage_note) out.push({ t: 'note', text: reason.coverage_note });
  if (showPct && pb.benchmarking_note) out.push({ t: 'note', text: pb.benchmarking_note });
  if (!showPct) out.push({ t: 'note', text: 'Percentiles hidden — fewer than 10 comparable candidates so far.' });
  const rfInfo = c.role_frame || assessment?.role_frame;
  if (rfInfo?.missing) out.push({ t: 'warn', text: roleFrameText(rfInfo) });
  if (c.human_review || c.fairness_check?.passed === false) {
    out.push({ t: 'warn', text: c.human_review || 'Needs human review — protected-characteristic wording was removed from this AI report.' });
  }
  if (evidence === 'unverified') {
    out.push({ t: 'warn', text: 'Unverified — possible assistance. Integrity signals were high, so the skills, strengths and red-flag results below describe what the candidate said, not verified ability.' });
  } else if (evidence === 'verify_next_round') {
    out.push({ t: 'warn', text: 'Some integrity signals were raised — verify the key strengths below in the next round.' });
  }

  // 2. Candidate profile
  if (c.candidate_profile_summary) out.push({ t: 'h', text: 'Candidate Profile' }, { t: 'p', text: c.candidate_profile_summary });

  // 3. Areas assessed
  if (c.areas_assessed?.length > 0) {
    out.push({ t: 'h', text: 'Areas Assessed' });
    out.push({
      t: 'table',
      head: ['Area', 'Score', 'Points', 'Weight', ...(showPct ? ['Role %ile', 'All-IT %ile'] : []), 'Assessment'],
      rows: c.areas_assessed.map((a) => [
        a.area_name ?? '', `${Number(a.score_0_5 ?? 0).toFixed(1)} / 5`,
        `${a.achieved_points ?? '—'} / ${a.allocated_points ?? '—'}`, a.weight_pct != null ? `${a.weight_pct}%` : '—',
        ...(showPct ? [a.role_percentile != null ? `${a.role_percentile}th` : '—',
                       a.all_roles_percentile != null ? `${a.all_roles_percentile}th` : '—'] : []),
        a.area_assessment ?? '—',
      ]),
    });
  }

  // 4. Summary — strengths + weaknesses
  if (c.summary) {
    out.push({ t: 'h', text: 'Interview Summary' });
    if (c.summary.full_summary) out.push({ t: 'p', text: c.summary.full_summary });
    if (c.summary.strengths?.length > 0) {
      out.push({ t: 'sub', text: 'Strengths' });
      out.push({ t: 'bullets', items: c.summary.strengths.map((s) => [
        s.area || (typeof s === 'string' ? s : ''),
        (s.verification || evidence === 'unverified') ? `[${s.verification || 'unverified — possible assistance'}]` : '',
        s.evidence ? q(s.evidence) : '', s.impact ? `→ ${s.impact}` : '',
      ].filter(Boolean).join(' — ')) });
    }
    if (c.summary.weaknesses?.length > 0) {
      out.push({ t: 'sub', text: 'Weaknesses' });
      out.push({ t: 'bullets', items: c.summary.weaknesses.map((w) => [
        w.area || (typeof w === 'string' ? w : ''), w.evidence ? q(w.evidence) : '', w.context ? `Context: ${w.context}` : '',
      ].filter(Boolean).join(' — ')) });
    }
  }

  // 4b. Not assessed
  if (c.not_assessed?.length > 0) {
    out.push({ t: 'h', text: 'Not assessed' }, { t: 'note', text: 'Not asked in this interview — neither a strength nor a weakness' });
    out.push({ t: 'bullets', items: c.not_assessed.map((n) => (n.topic || n) + (n.note ? ` — ${n.note}` : '')) });
  }

  // 4d. Second device
  const sd = c.second_device;
  if (sd?.summary || sd?.not_monitored) {
    out.push({ t: 'h', text: sd.review ? reviewLabel(sd.timeline)
      : sd.declined ? 'Second-device monitoring declined by candidate' : 'Camera and answer signals (information only)' });
    out.push({ t: 'note', text: 'Not proof — review the recording at these times' });
    if (sd.summary) out.push({ t: 'p', text: sd.summary });
    if ((sd.timeline || []).length > 0) {
      out.push({ t: 'bullets', items: sd.timeline.map((e) => `${e.start}–${e.end}  ${e.label} (${e.duration_s} s`
        + `${e.confidence ? `, confidence ${Math.round(e.confidence * 100)}%` : ''})`) });
    }
    if (sd.not_monitored) out.push({ t: 'note', text: `Not monitored: ${sd.not_monitored}` });
  }

  // 4c. Interrupted
  if (c.interrupted?.length > 0) {
    out.push({ t: 'h', text: 'Interrupted by the interviewer — not fully assessed' },
      { t: 'note', text: 'The interviewer moved on before the candidate finished — not scored as a missing answer' });
    out.push({ t: 'bullets', items: c.interrupted.map((it) => `${it.time ? `${it.time}  ` : ''}${it.question}${it.why ? ` — ${it.why}` : ''}`) });
  }

  // 5. Problem breakdown
  if (c.problem_breakdown?.length > 0) {
    out.push({ t: 'h', text: 'Problem Breakdown' });
    c.problem_breakdown.forEach((p, i) => {
      const n = p.problem_number ?? i + 1;
      out.push({ t: 'sub', text: `Problem ${n}${p.category ? ` · ${p.category}` : ''} — ${Number(p.problem_score ?? 0).toFixed(1)} / 5` });
      if (p.question_asked) out.push({ t: 'p', text: q(p.question_asked), style: 'italic' });
      const src = (questionSources || []).find((s) => String(s.problem_number) === String(n));
      if (src?.rationale) out.push({ t: 'note', text: `Why this question: ${src.rationale}` });
      if (p.criteria_met?.length > 0) out.push({ t: 'p', text: 'Criteria met', style: 'bold' }, { t: 'bullets', items: p.criteria_met.map((x) => `✓ ${x}`) });
      if (p.criteria_missed?.length > 0) out.push({ t: 'p', text: 'Criteria missed', style: 'bold' }, { t: 'bullets', items: p.criteria_missed.map((x) => `✗ ${x}`) });
      if (p.interviewer_thoughts) out.push({ t: 'p', text: `Interviewer observations: ${p.interviewer_thoughts}` });
      if (p.key_quote) out.push({ t: 'p', text: `Key quote: ${q(p.key_quote)}`, style: 'italic' });
    });
  }

  // 6. Risks
  if (c.risks?.length > 0) {
    out.push({ t: 'h', text: 'Risk Assessment' });
    c.risks.forEach((r) => {
      const mit = (r.mitigability || r.risk_area || '').toUpperCase();
      const label = mit.includes('HIGH') ? 'HIGH' : (mit.includes('MEDIUM') || mit.includes('MED')) ? 'MEDIUM' : 'LOW';
      out.push({ t: 'sub', text: `${r.risk_area || r.area} — ${label} mitigability` });
      if (r.evidence) out.push({ t: 'p', text: q(r.evidence), style: 'italic' });
      if (r.mitigability_rationale) out.push({ t: 'p', text: r.mitigability_rationale });
      if (r.next_round_probe) out.push({ t: 'p', text: `Next round probe: ${q(r.next_round_probe)}` });
    });
  }

  // 7. Red flags (answers under integrity risk can't clear a flag — as ReportView)
  if (c.red_flag_assessment?.length > 0) {
    out.push({ t: 'h', text: 'Red Flag Assessment' });
    out.push({
      t: 'table', head: ['Flag', 'Severity', 'Evidence', 'Recommendation'],
      rows: c.red_flag_assessment.map((f) => {
        const raw = (f.severity || '').toUpperCase();
        const sev = evidence === 'unverified' && raw === 'CLEARED' ? 'UNVERIFIED' : raw;
        return [f.flag ?? '', sev + (f.observed === false ? ' (not observed)' : ''), f.evidence ?? '', f.recommendation ?? ''];
      }),
    });
  }

  // 8. Next round guidance
  if (c.next_round_guidance?.length > 0) {
    out.push({ t: 'h', text: 'Next Round Guidance' });
    out.push({ t: 'bullets', items: c.next_round_guidance.map((g) => [
      g.area ? `${g.area}:` : '', g.specific_question || (typeof g === 'string' ? g : ''), g.why ? `(Why: ${g.why})` : '',
    ].filter(Boolean).join(' ')) });
  }

  // 9. Integrity + rationale
  const ia = c.integrity_assessment;
  if (ia) {
    out.push({ t: 'h', text: 'Integrity Assessment' });
    out.push({ t: 'kv', rows: [['Integrity confidence', String(ia.confidence_level ?? '—')], ['Risk level', riskLevelLabel(ia.risk_level, c.second_device)]] });
    out.push({ t: 'note', text: INTEGRITY_CONFIDENCE_HELP });
    if (ia.proctoring_summary) out.push({ t: 'p', text: ia.proctoring_summary });
  }
  if (c.recommendation_rationale) out.push({ t: 'h', text: 'Recommendation Rationale' }, { t: 'p', text: c.recommendation_rationale });

  if (!isHM && c.role_fit_summary) {
    out.push({ t: 'h', text: 'Role Fit' }, { t: 'p', text: c.role_fit_summary });
    if (c.suggested_next_steps) out.push({ t: 'p', text: `Next steps: ${c.suggested_next_steps}` });
  }
  if (!isHM && c.talking_points_for_hm?.length > 0) {
    out.push({ t: 'h', text: 'Talking Points for the Hiring Manager' },
      { t: 'bullets', items: c.talking_points_for_hm.map((x) => (typeof x === 'string' ? x : x.point || JSON.stringify(x))) });
  }
  const frameText = rfInfo?.missing ? '' : roleFrameText(rfInfo);
  if (isHM && frameText) out.push({ t: 'note', text: frameText });
  if (isHM) out.push({ t: 'note', text: ADVISORY_FOOTER });
  return out;
}

export function reportFileName(interview, typeLabel, ext) {
  const name = String(interview?.candidate_name || 'Candidate').replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '');
  const kind = typeLabel === 'Hiring Manager' ? 'HM_Report' : 'Recruiter_Report';
  return `${name}_${kind}_${interview?.id ?? ''}.${ext}`.replace(/_\./, '.');
}

const titleLines = (interview, typeLabel) => [
  `Quali5d — ${typeLabel} Report`,
  [interview?.candidate_name, interview?.role_type, interview?.tech_stack, interview?.interview_level].filter(Boolean).join(' • '),
];

// ── PDF ──
// jsPDF's built-in fonts only cover Windows-1252: map common symbols, drop emoji, '?' for the rest.
const CP1252_EXTRA = '€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ';
const PDF_MAP = { '→': '->', '←': '<-', '✓': '+', '✗': 'x', '≥': '>=', '≤': '<=', '−': '-', '≈': '~', ' ': ' ', ' ': ' ' };
export function pdfSafe(s) {
  return Array.from(String(s ?? '')).map((ch) => {
    if (PDF_MAP[ch]) return PDF_MAP[ch];
    const code = ch.codePointAt(0);
    if (code === 10 || (code >= 32 && code <= 126) || (code >= 160 && code <= 255) || CP1252_EXTRA.includes(ch)) return ch;
    if (code >= 0x1f000 || (code >= 0x2600 && code <= 0x27bf) || code === 0xfe0f) return '';   // emoji / dingbats
    return '?';
  }).join('');
}

export async function reportToPdf(blocks, { interview, typeLabel = 'Hiring Manager' } = {}) {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 15;
  const maxW = W - 2 * M;
  let y = 0;

  const [title, sub] = titleLines(interview, typeLabel);
  doc.setFillColor(0, 61, 130);                                   // brand-dark
  doc.rect(0, 0, W, 24, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.text(pdfSafe(title), M, 11);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.text(pdfSafe(sub), M, 18, { maxWidth: maxW });
  y = 32;

  const ensure = (h) => { if (y + h > H - 18) { doc.addPage(); y = 18; } };
  const write = (text, { size = 10, style = 'normal', color = [55, 65, 81], indent = 0, gap = 2 } = {}) => {
    doc.setFont('helvetica', style); doc.setFontSize(size); doc.setTextColor(...color);
    const lh = size * 0.45;
    doc.splitTextToSize(pdfSafe(text), maxW - indent).forEach((line) => { ensure(lh); doc.text(line, M + indent, y); y += lh; });
    y += gap;
  };

  blocks.forEach((b) => {
    if (b.t === 'h') { y += 3; ensure(14); write(b.text, { size: 13, style: 'bold', color: [0, 61, 130] });
      doc.setDrawColor(0, 169, 157); doc.line(M, y - 1.5, W - M, y - 1.5); y += 3; }
    else if (b.t === 'sub') write(b.text, { size: 11, style: 'bold', color: [17, 24, 39] });
    else if (b.t === 'p') write(b.text, { style: b.style === 'italic' ? 'italic' : b.style === 'bold' ? 'bold' : 'normal' });
    else if (b.t === 'note') write(b.text, { size: 9, color: [107, 114, 128] });
    else if (b.t === 'warn') write(b.text, { style: 'bold', color: [146, 64, 14] });
    else if (b.t === 'bullets') b.items.forEach((it) => write(`•  ${it}`, { indent: 2, gap: 1 }));
    else if (b.t === 'kv' || b.t === 'table') {
      autoTable(doc, {
        startY: y, margin: { left: M, right: M },
        head: b.t === 'table' ? [b.head.map(pdfSafe)] : undefined,
        body: b.rows.map((r) => r.map(pdfSafe)),
        theme: b.t === 'table' ? 'grid' : 'plain',
        styles: { font: 'helvetica', fontSize: 9, cellPadding: 1.8, textColor: [55, 65, 81], overflow: 'linebreak' },
        headStyles: { fillColor: [0, 102, 204], textColor: 255, fontStyle: 'bold' },
        columnStyles: b.t === 'kv' ? { 0: { fontStyle: 'bold', cellWidth: 45 } } : {},
      });
      y = doc.lastAutoTable.finalY + 4;
    }
  });

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(156, 163, 175);
    doc.text(pdfSafe(ADVISORY_FOOTER), W / 2, H - 12, { align: 'center' });
    doc.text(`Confidential — Quali5d · Page ${i} of ${pages}`, W / 2, H - 8, { align: 'center' });
  }
  return doc;
}

// ── DOCX (HTML → html-docx-js, the same converter as the Phase 1 downloads) ──
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export function reportToHtml(blocks, { interview, typeLabel = 'Hiring Manager' } = {}) {
  const [title, sub] = titleLines(interview, typeLabel);
  const body = blocks.map((b) => {
    if (b.t === 'h') return `<h2>${esc(b.text)}</h2>`;
    if (b.t === 'sub') return `<h3>${esc(b.text)}</h3>`;
    if (b.t === 'p') return b.style === 'italic' ? `<p><em>${esc(b.text)}</em></p>`
      : b.style === 'bold' ? `<p><strong>${esc(b.text)}</strong></p>` : `<p>${esc(b.text)}</p>`;
    if (b.t === 'note') return `<p class="note">${esc(b.text)}</p>`;
    if (b.t === 'warn') return `<p class="warn">${esc(b.text)}</p>`;
    if (b.t === 'bullets') return `<ul>${b.items.map((it) => `<li>${esc(it)}</li>`).join('')}</ul>`;
    if (b.t === 'kv') return `<table class="kv">${b.rows.map(([k, v]) => `<tr><td><strong>${esc(k)}</strong></td><td>${esc(v)}</td></tr>`).join('')}</table>`;
    if (b.t === 'table') {
      return `<table class="grid"><tr>${b.head.map((h) => `<th>${esc(h)}</th>`).join('')}</tr>`
        + `${b.rows.map((r) => `<tr>${r.map((v) => `<td>${esc(v)}</td>`).join('')}</tr>`).join('')}</table>`;
    }
    return '';
  }).join('\n');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
    body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; line-height: 1.4; color: #374151; }
    h1 { font-size: 18pt; color: #003D82; margin: 0; } h2 { font-size: 14pt; color: #003D82; margin-top: 16pt; border-bottom: 1px solid #00A99D; }
    h3 { font-size: 12pt; color: #111827; margin-top: 10pt; } .sub { color: #6b7280; } .note { font-size: 9pt; color: #6b7280; }
    .warn { font-weight: bold; color: #92400e; } table { border-collapse: collapse; width: 100%; margin: 6pt 0; }
    table.grid th { background: #0066CC; color: #ffffff; text-align: left; } table.grid td, table.grid th { border: 1px solid #d1d5db; padding: 4pt; font-size: 9pt; vertical-align: top; }
    table.kv td { padding: 2pt 6pt 2pt 0; }
  </style></head><body><h1>${esc(title)}</h1><p class="sub">${esc(sub)}</p>\n${body}</body></html>`;
}

export async function reportToDocx(blocks, opts) {
  const htmlDocx = await import('html-docx-js/dist/html-docx');
  return (htmlDocx.default || htmlDocx).asBlob(reportToHtml(blocks, opts));
}

function saveBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

// Entry point for the download buttons
export async function downloadReport(format, { report, interview, typeLabel = 'Hiring Manager' }) {
  const blocks = buildReportBlocks(parseReportContent(report), {
    typeLabel, assessment: interview?.assessment, questionSources: interview?.question_sources,
  });
  const opts = { interview, typeLabel };
  if (format === 'pdf') {
    (await reportToPdf(blocks, opts)).save(reportFileName(interview, typeLabel, 'pdf'));
  } else {
    saveBlob(await reportToDocx(blocks, opts), reportFileName(interview, typeLabel, 'docx'));
  }
}
